// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// Types and API
import { addPatient } from "../api";
import { AddEditPatientType } from "../types";

export const useAddPatient = (clinicId: string | undefined) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();

  return useMutation({
    mutationFn: async (patientData: AddEditPatientType) => {
      if (!clinicId) throw new Error();

      try {
        const token = await getToken();
        return addPatient(token, clinicId, patientData);
      } catch (error) {
        throw error;
      }
    },
    // Invalidate patients query cache
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patients", clinicId] });
      setSnackbarInfo({
        variant: "success",
        message: t("Patient added successfully!"),
      });
    },
    onError: () => {
      setSnackbarInfo({
        variant: "error",
        message: t("Patient could not be added!"),
      });
    },
  });
};
